import React from 'react';
import { WorkflowRun } from '../../types/workflow';

const STATUS_BG: Record<string, { bg: string; color: string }> = {
  succeeded: { bg: '#E9F6F6', color: '#0F6E56' },
  failed:    { bg: '#FEE2E2', color: '#b91c1c' },
  running:   { bg: '#E0F2FE', color: '#075985' },
  retrying:  { bg: '#FEF3C7', color: '#92400e' },
};

const NODE_STATUS_BG: Record<string, string> = {
  success: '#10b981',
  failed:  '#ef4444',
  retried: '#f59e0b',
  running: '#0ea5e9',
  idle:    '#94a3b8',
};

function fmtDur(ms: number) {
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(2)}s`;
}

export function RunDetailDrawer({ run, onClose }: { run: WorkflowRun; onClose: () => void }) {
  const st = STATUS_BG[run.status] || STATUS_BG.succeeded;
  const total = run.duration_ms || 1;
  return (
    <div className="fixed inset-0 z-40 flex justify-end" onClick={onClose}>
      <div className="fixed inset-0 bg-black/30" />
      <aside
        className="relative w-[640px] max-w-[95vw] bg-white h-full overflow-y-auto shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-trim flex items-center justify-between sticky top-0 bg-white z-10">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-sm font-semibold text-ink truncate">{run.run_id}</h3>
              <span className="px-2 py-0.5 rounded-full text-xs font-medium" style={{ background: st.bg, color: st.color }}>
                {run.status}
              </span>
            </div>
            <div className="text-xs text-muted mt-0.5 font-mono">trace={run.trace_id}</div>
          </div>
          <button onClick={onClose} className="text-muted hover:text-ink text-lg">×</button>
        </div>

        <div className="p-5 space-y-5">
          <section className="grid grid-cols-2 gap-3 text-xs">
            <div className="bg-wash rounded p-2.5"><div className="text-muted">Started</div><div className="text-ink mt-0.5">{new Date(run.started_at).toLocaleString()}</div></div>
            <div className="bg-wash rounded p-2.5"><div className="text-muted">Completed</div><div className="text-ink mt-0.5">{run.completed_at ? new Date(run.completed_at).toLocaleString() : '—'}</div></div>
            <div className="bg-wash rounded p-2.5"><div className="text-muted">Duration</div><div className="text-ink mt-0.5 font-mono">{fmtDur(run.duration_ms)}</div></div>
            <div className="bg-wash rounded p-2.5"><div className="text-muted">Trigger</div><div className="text-ink mt-0.5">{run.trigger_source}</div></div>
            <div className="bg-wash rounded p-2.5"><div className="text-muted">Initiated by</div><div className="text-ink mt-0.5">{run.initiated_by}</div></div>
            <div className="bg-wash rounded p-2.5"><div className="text-muted">Retries</div><div className="text-ink mt-0.5 font-mono">{run.retry_count}</div></div>
          </section>

          <section>
            <div className="text-sm font-semibold text-ink mb-2">Execution timeline</div>
            <div className="space-y-1.5">
              {run.node_timeline.map(n => {
                const widthPct = Math.max(2, (n.duration_ms / total) * 100);
                return (
                  <div key={n.node_id} className="flex items-center gap-2 text-xs">
                    <div className="w-32 truncate text-copy">{n.node_label}</div>
                    <div className="flex-1 bg-wash h-3 rounded overflow-hidden">
                      <div className="h-full" style={{ width: `${widthPct}%`, background: NODE_STATUS_BG[n.status] || '#94a3b8' }} />
                    </div>
                    <div className="w-16 text-right text-muted font-mono">{fmtDur(n.duration_ms)}</div>
                  </div>
                );
              })}
            </div>
          </section>

          <section>
            <div className="text-sm font-semibold text-ink mb-2">Logs</div>
            <ul className="space-y-1">
              {run.logs.map((l, i) => (
                <li key={i} className="text-xs font-mono bg-wash rounded p-1.5">
                  <span className="text-muted">{new Date(l.ts).toLocaleTimeString()}</span>{' '}
                  <span style={{ color: l.level === 'error' ? '#b91c1c' : l.level === 'warn' ? '#92400e' : '#0F6E56' }}>{l.level.toUpperCase()}</span>{' '}
                  <span className="text-copy">{l.message}</span>
                </li>
              ))}
            </ul>
          </section>

          <section>
            <div className="text-sm font-semibold text-ink mb-2">Payloads</div>
            <div className="grid grid-cols-1 gap-2">
              <div>
                <div className="text-xs text-muted mb-1">Input</div>
                <pre className="text-xs font-mono bg-wash p-2 rounded text-copy whitespace-pre-wrap">{JSON.stringify(run.payloads.input, null, 2)}</pre>
              </div>
              <div>
                <div className="text-xs text-muted mb-1">Output</div>
                <pre className="text-xs font-mono bg-wash p-2 rounded text-copy whitespace-pre-wrap">{JSON.stringify(run.payloads.output, null, 2)}</pre>
              </div>
            </div>
          </section>
        </div>
      </aside>
    </div>
  );
}
